/**
 * File Preview Item Component
 * Shows individual file preview before upload
 */

import { useEffect, useState } from 'react';
import {
  getFileCategory,
  formatFileSize,
  getFileIcon,
} from '@/utils/file-validation.util';

interface FilePreviewItemProps {
  file: File;
  error?: string;
  progress?: number;
  onRemove: () => void;
}

const FilePreviewItem = ({
  file,
  error,
  progress,
  onRemove,
}: FilePreviewItemProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const category = getFileCategory(file);

  // Generate preview for images
  useEffect(() => {
    if (category === 'image') {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
    // Note: previewUrl is a FileReader data URL (not an object URL), so there is
    // nothing to revoke on cleanup.
  }, [file, category]);

  return (
    <div className="relative flex items-center space-x-3 p-2 bg-gray-50 rounded-lg border border-gray-200">
      {/* Preview/Icon */}
      <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-gray-100 rounded overflow-hidden">
        {category === 'image' && previewUrl ? (
          <img
            src={previewUrl}
            alt={file.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="text-2xl">{getFileIcon(file.name)}</span>
        )}
      </div>

      {/* File info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">
          {file.name}
        </p>
        <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>

        {/* Error message */}
        {error && <p className="text-xs text-red-600 mt-1">{error}</p>}

        {/* Upload progress */}
        {progress !== undefined && progress < 100 && !error && (
          <div className="mt-1">
            <div className="flex items-center space-x-2">
              <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                <div
                  className="bg-blue-600 h-1.5 rounded-full transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-xs text-gray-500">{progress}%</span>
            </div>
          </div>
        )}

        {/* Completed indicator */}
        {progress === 100 && !error && (
          <p className="text-xs text-green-600 mt-1 flex items-center space-x-1">
            <svg
              className="w-3 h-3"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span>Качено успешно</span>
          </p>
        )}
      </div>

      {/* Remove button */}
      <button
        type="button"
        onClick={onRemove}
        className="flex-shrink-0 p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
        aria-label="Премахни файл"
        title="Премахни файл"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  );
};

export default FilePreviewItem;
